import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Video, PhoneOff, Sparkles, Wand2 } from "lucide-react";

/**
 * VoiceTutoring — product-grade video call demo.
 *  Left tile: AURA tutor (animated glowing avatar + waveform).
 *  Right tile: You (you-initials + live mic waveform).
 *  Bottom: live captions, inline correction chip, and a "memory saved" toast.
 */

const SCRIPT = [
  { who: "you", text: "Yesterday I go to the market and I buy some fruits.", caption: "Yesterday I go to the market…" },
  {
    who: "aura",
    text: "Close — same pattern we saw Tuesday. Try: Yesterday I went to the market and bought some fruits.",
    correction: {
      was: "I go to the market and I buy",
      nowText: "I went to the market and bought",
      rule: "Past simple (irregular verbs)",
    },
  },
  { who: "you", text: "Yesterday I went to the market and bought some fruits.", caption: "Yesterday I went to the market and bought some fruits." },
  {
    who: "aura",
    text: "Perfect. I'll bring this back in three days inside a restaurant scene.",
    memorySaved: { label: "Past simple · market scene", cefr: "B1 grammar" },
  },
];

// stable pseudo-random amplitudes for waveforms
const wave = (seed, n, base = 0.3, variance = 0.7) => {
  const out = [];
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280;
    out.push(base + (s / 233280) * variance);
  }
  return out;
};

const LiveWave = ({ bars = 42, active, tint = "cyan" }) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf;
    const loop = () => {
      setT((v) => v + 0.08);
      raf = requestAnimationFrame(loop);
    };
    if (active) raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const base = wave(tint === "cyan" ? 17 : 31, bars);
  return (
    <div className="flex items-end gap-[3px] h-9 w-full justify-center">
      {base.map((a, i) => {
        const h = active
          ? 10 + Math.abs(Math.sin(t + i * 0.4)) * 26 * a + Math.random() * 5
          : 4 + a * 4;
        return (
          <span
            key={i}
            className="block w-[3px] rounded-full"
            style={{
              height: `${h}px`,
              background:
                tint === "cyan"
                  ? "linear-gradient(180deg, rgb(var(--accent-cyan-rgb)), rgb(var(--accent-blue-rgb)))"
                  : "linear-gradient(180deg, rgb(var(--accent-violet-rgb)), rgb(var(--accent-blue-rgb)))",
              opacity: active ? 0.9 : 0.35,
              transition: "height 120ms ease, opacity 220ms ease",
            }}
          />
        );
      })}
    </div>
  );
};

const AuraAvatar = ({ speaking }) => (
  <div className="relative w-32 h-32 sm:w-36 sm:h-36 grid place-items-center">
    <motion.div
      className="absolute inset-0 rounded-full"
      style={{ background: "radial-gradient(closest-side, rgb(var(--accent-cyan-rgb) / 0.55), transparent 70%)" }}
      animate={{ scale: speaking ? [1, 1.08, 1] : 1, opacity: speaking ? [0.55, 0.9, 0.55] : 0.4 }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute inset-3 rounded-full border border-aura-cyan/30"
      animate={{ rotate: 360 }}
      transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
    />
    <motion.div
      className="absolute inset-6 rounded-full border border-aura-cyan/20"
      animate={{ rotate: -360 }}
      transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
    />
    <div className="relative w-16 h-16 rounded-full glass edge-glow grid place-items-center">
      <div className="w-2.5 h-2.5 rounded-full bg-aura-cyan shadow-[0_0_16px_rgb(var(--accent-cyan-rgb)/0.9)]" />
    </div>
  </div>
);

const YouAvatar = ({ speaking }) => (
  <div className="relative w-32 h-32 sm:w-36 sm:h-36 grid place-items-center">
    <motion.div
      className="absolute inset-0 rounded-full"
      style={{ background: "radial-gradient(closest-side, rgb(var(--accent-violet-rgb) / 0.45), transparent 70%)" }}
      animate={{ scale: speaking ? [1, 1.08, 1] : 1, opacity: speaking ? [0.4, 0.75, 0.4] : 0.3 }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative w-24 h-24 rounded-full glass hairline grid place-items-center overflow-hidden">
      <span className="font-display font-light text-[40px] text-ink/90 tracking-tighter">M</span>
      <div className="absolute inset-0 rounded-full border border-panel/10" />
    </div>
  </div>
);

const VoiceTutoring = () => {
  const [idx, setIdx] = useState(0);
  const [time, setTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const pausedRef = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setTime((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      if (!pausedRef.current) setIdx((i) => (i + 1) % SCRIPT.length);
    }, 3200);
    return () => clearInterval(t);
  }, []);

  const current = SCRIPT[idx];
  const mm = String(Math.floor(time / 60)).padStart(2, "0");
  const ss = String(time % 60).padStart(2, "0");

  return (
    <section id="voice" className="relative py-28 px-6" data-testid="section-voice">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="section-label">Live voice tutoring</div>
            <h2 className="headline mt-4 text-4xl sm:text-5xl lg:text-[72px] max-w-3xl">
              Real conversation.<br /><em>Not</em> passive exercises.
            </h2>
          </div>
          <p className="text-whisper text-[16px] leading-[1.55] max-w-md">
            Tap once. Speak. AURA listens, corrects in the rhythm of a real call, and
            remembers what mattered.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-14 glass rounded-[28px] edge-glow overflow-hidden relative"
          data-testid="voice-card"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
        >
          {/* Ambient */}
          <div aria-hidden className="absolute -top-28 -left-24 w-[420px] h-[420px] rounded-full bg-aura-cyan/15 blur-[140px]" />
          <div aria-hidden className="absolute -bottom-28 -right-24 w-[420px] h-[420px] rounded-full bg-aura-violet/12 blur-[140px]" />

          {/* Header bar */}
          <div className="relative px-5 sm:px-8 py-4 border-b border-panel/6 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="relative inline-block w-2 h-2 rounded-full bg-red-400 shadow-[0_0_10px_rgb(248,113,113,0.7)]">
                <span className="absolute inset-0 rounded-full bg-red-400/60 animate-ping" />
              </span>
              <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-ink">Live · Spanish (MX)</span>
              <span className="hidden sm:inline font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                Continuing from Tue · past simple
              </span>
            </div>
            <div className="flex items-center gap-3 font-mono text-[11px] tabular-nums text-whisper">
              <span>{mm}:{ss}</span>
              <span className="pill">B1 · in progress</span>
            </div>
          </div>

          {/* Video call grid */}
          <div className="relative grid lg:grid-cols-2 gap-4 p-5 sm:p-8" data-testid="voice-call-grid">
            {/* TUTOR TILE */}
            <div className="relative rounded-2xl border border-panel/8 overflow-hidden min-h-[360px] bg-elev/60">
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 55% at 50% 40%, rgb(var(--accent-cyan-rgb) / 0.18), transparent 70%)",
                }}
              />
              <div className="absolute top-3 left-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper">
                <span className="w-1.5 h-1.5 rounded-full bg-aura-cyan" />
                AURA · tutor
              </div>
              <div className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                HD · 0.31s
              </div>
              <div className="relative h-full grid place-items-center py-12">
                <AuraAvatar speaking={current.who === "aura"} />
              </div>
              <div className="absolute bottom-0 inset-x-0 px-4 pb-3">
                <LiveWave active={current.who === "aura"} tint="cyan" />
              </div>
            </div>

            {/* YOU TILE */}
            <div className="relative rounded-2xl border border-panel/8 overflow-hidden min-h-[360px] bg-elev/60">
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 55% at 50% 40%, rgb(var(--accent-violet-rgb) / 0.14), transparent 70%)",
                }}
              />
              <div className="absolute top-3 left-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper">
                <span className="w-1.5 h-1.5 rounded-full bg-aura-violet" />
                You · B1 learner
              </div>
              <div className="absolute top-3 right-3 flex items-center gap-2">
                <span className="pill bg-panel/[0.04] border-panel/10 text-whisper">muted? {muted ? "yes" : "no"}</span>
              </div>
              <div className="relative h-full grid place-items-center py-12">
                <YouAvatar speaking={current.who === "you"} />
              </div>
              <div className="absolute bottom-0 inset-x-0 px-4 pb-3">
                <LiveWave active={!muted && current.who === "you"} tint="violet" />
              </div>
            </div>
          </div>

          {/* Captions + correction strip */}
          <div className="relative px-5 sm:px-8 pb-5">
            <div className="relative rounded-2xl border border-panel/8 bg-elev/60 p-5 min-h-[120px]">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                Live caption · {current.who === "aura" ? "AURA" : "You"}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35 }}
                  className="mt-2 font-serif text-[22px] sm:text-[24px] leading-[1.3] text-ink"
                >
                  {current.caption || current.text}
                </motion.div>
              </AnimatePresence>

              {/* Correction chip */}
              <AnimatePresence>
                {current.correction && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.35 }}
                    className="mt-4 inline-flex flex-wrap items-center gap-3 rounded-full border border-aura-cyan/30 bg-aura-cyan/[0.06] pl-1.5 pr-4 py-1.5"
                    data-testid="voice-correction-chip"
                  >
                    <span className="flex items-center gap-1.5 rounded-full bg-aura-cyan text-[#02121A] px-3 py-1 font-mono text-[10px] tracking-[0.18em] uppercase">
                      <Wand2 size={11} /> fix
                    </span>
                    <span className="font-mono text-[11px] text-whisper line-through">{current.correction.was}</span>
                    <span className="text-whisper-muted">→</span>
                    <span className="font-mono text-[11px] text-ink">{current.correction.nowText}</span>
                    <span className="text-whisper-muted font-mono text-[10px] tracking-[0.18em] uppercase">·</span>
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-aura-cyan">{current.correction.rule}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Memory toast */}
              <AnimatePresence>
                {current.memorySaved && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.4 }}
                    className="mt-4 inline-flex items-center gap-3 rounded-full border border-aura-violet/30 bg-aura-violet/[0.08] px-4 py-2"
                    data-testid="voice-memory-toast"
                  >
                    <Sparkles size={12} className="text-aura-violet" />
                    <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-ink/90">
                      Memory saved · {current.memorySaved.label}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                      {current.memorySaved.cefr}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Call controls */}
          <div className="relative px-5 sm:px-8 pb-6 flex items-center justify-between flex-wrap gap-3 border-t border-panel/6 pt-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMuted((m) => !m)}
                className="w-11 h-11 rounded-full grid place-items-center border border-panel/10 bg-panel/[0.03] hover:border-aura-cyan/60 transition-colors"
                aria-label="Mute"
                data-testid="voice-mute-button"
              >
                {muted ? <MicOff size={15} /> : <Mic size={15} />}
              </button>
              <button className="w-11 h-11 rounded-full grid place-items-center border border-panel/10 bg-panel/[0.03]" aria-label="Video">
                <Video size={15} />
              </button>
              <button className="w-11 h-11 rounded-full grid place-items-center border border-red-400/40 bg-red-500/10 text-red-300" aria-label="End">
                <PhoneOff size={15} />
              </button>
            </div>
            <div className="flex items-center gap-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
              <span className="pill">Next: review in 72h</span>
              <span className="hidden sm:inline">· auto-scheduled</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VoiceTutoring;
